import React, { useEffect, useState } from "react";
import NewsCard from "../common/NewsCard";
import AdCard from "../common/AdCard";
import { getAdsForSection } from "../../config/ads";
import { Link } from "react-router-dom";

function NewsSection({ title, articles, category }) {
  const [masterArticle, setMasterArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadHomeMaster() {
      try {
        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/homemasters`;
        const res = await fetch(apiUrl);
        const data = await res.json();
        const matched = data.find(
          (item) => item.HM_Section?.toLowerCase() === category?.toLowerCase()
        );
        setMasterArticle(matched || null);
      } catch (err) {
        console.error("HomeMaster API error:", err);
        setMasterArticle(null);
      } finally {
        setLoading(false);
      }
    }
    loadHomeMaster();
  }, [category]);

  if (!articles || articles.length === 0) {
    return (
      <div style={{ marginBottom: "35px" }}>
        <div className="ns-section-title">{title}</div>
        <p style={{ color: "#777", padding: "20px" }}>No articles available</p>
      </div>
    );
  }

  const bigCard = masterArticle || articles[0];
  const isMasterCard = !!masterArticle;
  const gridArticles = isMasterCard
    ? articles.slice(0, 12)
    : articles.slice(1, 13);
  const { ad1, ad2 } = getAdsForSection(category || title);

  return (
    <>
      <div style={{ marginBottom: "40px" }}>
        <div className="ns-section-title">{title}</div>

        {/* ── DESKTOP LAYOUT ── */}
        <div className="ns-desktop-layout">
          {/* ROW 1: Big Card + Ad1 */}
          <div className="ns-row-1">
            <div className="ns-card ns-big-card">
              {isMasterCard ? (
                <Link
                  to={`/homemaster/${bigCard.id}`}
                  className="ns-big-card-link"
                >
                  <BigCardContent bigCard={bigCard} isMasterCard />
                </Link>
              ) : (
                <BigCardContent bigCard={bigCard} isMasterCard={false} />
              )}
            </div>
            <div className="ns-ad-column">{ad1 && <AdCard ad={ad1} />}</div>
          </div>

          {/* ROW 2: 4 articles */}
          <div className="ns-news-row ns-row-4col">
            {gridArticles.slice(0, 4).map((article, i) => (
              <NewsCard key={`r2-${i}`} article={article} />
            ))}
          </div>

          {/* ROW 3: Ad2 + 3 articles */}
          <div className="ns-news-row ns-row-4col">
            {ad2 && (
              <div className="ns-ad-slot">
                <AdCard ad={ad2} />
              </div>
            )}
            {gridArticles.slice(4, 7).map((article, i) => (
              <NewsCard key={`r3-${i}`} article={article} />
            ))}
          </div>

          {/* ROW 4: 4 articles */}
          <div className="ns-news-row ns-row-4col">
            {gridArticles.slice(7, 11).map((article, i) => (
              <NewsCard key={`r4-${i}`} article={article} />
            ))}
          </div>
        </div>

        {/* ── MEDIUM DEVICE LAYOUT (3-col flat grid) ── */}
        <div className="ns-medium-layout">
          {/* Big card: full width on top */}
          <div className="ns-card ns-big-card ns-medium-big-card">
            {isMasterCard ? (
              <Link
                to={`/homemaster/${bigCard.id}`}
                className="ns-big-card-link"
              >
                <BigCardContent bigCard={bigCard} isMasterCard />
              </Link>
            ) : (
              <BigCardContent bigCard={bigCard} isMasterCard={false} />
            )}
          </div>

          {/* Flat 3-col grid: ad1 + all articles + ad2 mixed in naturally */}
          <div className="ns-medium-grid">
            {/* Ad1 as first card in grid */}
            {ad1 && (
              <div className="ns-medium-ad-slot">
                <AdCard ad={ad1} />
              </div>
            )}

            {/* Articles 0–2 */}
            {gridArticles.slice(0, 3).map((article, i) => (
              <NewsCard key={`m-a-${i}`} article={article} />
            ))}

            {/* Ad2 flows in after 3rd article */}
            {ad2 && (
              <div className="ns-medium-ad-slot">
                <AdCard ad={ad2} />
              </div>
            )}

            {/* Remaining articles */}
            {gridArticles.slice(3, 12).map((article, i) => (
              <NewsCard key={`m-b-${i}`} article={article} />
            ))}
          </div>
        </div>
      </div>

      <style>{`
        /* ── Section Title ── */
        .ns-section-title {
          margin: 30px 0 10px;
          font-size: 22px;
          font-weight: bold;
          color: #1a1a1a;
          border-left: 4px solid #0066cc;
          padding-left: 10px;
        }

        /* ── Base Card ── */
        .ns-card {
          background: #fff;
          border-radius: 10px;
          overflow: hidden;
          box-shadow: 0 2px 10px rgba(0,0,0,0.08);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        .ns-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 4px 16px rgba(0,0,0,0.12);
        }

        /* ════════════════════════════════
           DESKTOP LAYOUT (> 1024px)
        ════════════════════════════════ */
        .ns-desktop-layout { display: block; }
        .ns-medium-layout  { display: none; }

        /* Row 1 */
        .ns-row-1 {
          display: grid;
          grid-template-columns: 3fr 1fr;
          gap: 15px;
          margin-bottom: 15px;
          align-items: start;
        }

        /* Big card inner */
        .ns-big-card-link {
          text-decoration: none;
          color: inherit;
          display: block;
          height: 100%;
        }
        .ns-big-card-content {
          display: flex;
          height: 100%;
          min-height: 280px;
        }
        .ns-big-card-image {
          flex: 0 0 42%;
        }
        .ns-big-card-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .ns-big-card-body {
          padding: 20px;
          display: flex;
          flex-direction: column;
          flex: 1;
        }
        .ns-big-card-title {
          font-size: 20px;
          font-weight: bold;
          margin: 6px 0 12px;
          color: #222;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .ns-big-card-desc {
          font-size: 14px;
          color: #666;
          line-height: 1.6;
          display: -webkit-box;
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
          overflow: hidden;
          flex: 1;
        }
        .ns-big-card-readmore {
          margin-top: 14px;
          font-size: 14px;
          color: #0066cc;
          font-weight: 500;
          text-decoration: none;
        }

        .ns-ad-column {
          display: flex;
          align-items: stretch;
        }
        .ns-ad-column > * { width: 100%; height: 100%; }

        /* News rows */
        .ns-news-row {
          display: grid;
          gap: 15px;
          margin-bottom: 15px;
        }
        .ns-row-4col {
          grid-template-columns: repeat(4, 1fr);
        }
        .ns-ad-slot {
          display: flex;
          align-items: stretch;
        }
        .ns-ad-slot > * { width: 100%; height: 100%; }

        /* ════════════════════════════════
           MEDIUM LAYOUT (601px – 1024px)
           Big card full-width on top,
           then flat 3-col grid with ads
           as equal card slots
        ════════════════════════════════ */
        @media (max-width: 1024px) and (min-width: 601px) {
          .ns-desktop-layout { display: none; }
          .ns-medium-layout  { display: block; }

          /* Big card: full width, stacked (image top) */
          .ns-medium-big-card {
            margin-bottom: 15px;
            height: auto;
          }
          .ns-medium-big-card .ns-big-card-content {
            flex-direction: column;
            min-height: auto;
          }
          .ns-medium-big-card .ns-big-card-image {
            flex: none;
            height: 220px;
          }
          .ns-medium-big-card .ns-big-card-title {
            font-size: 18px;
          }
          .ns-medium-big-card .ns-big-card-desc {
            -webkit-line-clamp: 3;
          }

          /* 2-col flat grid — every child identical size */
          .ns-medium-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            grid-auto-rows: auto;
            align-items: stretch;
            gap: 15px;
          }

          /* Force every direct child (NewsCard root + ad slot) to full height */
          .ns-medium-grid > * {
            height: 100%;
            min-height: 280px;
          }

          /* Ad slot: matches news card exactly */
          .ns-medium-ad-slot {
            display: flex;
            align-items: stretch;
            height: 100%;
            min-height: 280px;
          }
          .ns-medium-ad-slot > * {
            width: 100%;
            height: 100%;
            min-height: 280px;
          }
        }

        /* ════════════════════════════════
           MOBILE (≤ 600px)
        ════════════════════════════════ */
        @media (max-width: 600px) {
          .ns-desktop-layout { display: none; }
          .ns-medium-layout  { display: block; }

          .ns-section-title { font-size: 18px; }

          .ns-medium-big-card {
            margin-bottom: 15px;
            height: auto;
          }
          .ns-medium-big-card .ns-big-card-content {
            flex-direction: column;
            min-height: auto;
          }
          .ns-medium-big-card .ns-big-card-image {
            flex: none;
            height: 190px;
          }
          .ns-medium-big-card .ns-big-card-title {
            font-size: 16px;
          }
          .ns-medium-big-card .ns-big-card-desc {
            font-size: 13px;
            -webkit-line-clamp: 3;
          }

          /* 2-col grid on mobile — equal height cards */
          .ns-medium-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            grid-auto-rows: auto;
            align-items: stretch;
            gap: 12px;
          }
          .ns-medium-grid > * {
            height: 100%;
            min-height: 240px;
          }
          .ns-medium-ad-slot {
            display: flex;
            align-items: stretch;
            height: 100%;
            min-height: 240px;
          }
          .ns-medium-ad-slot > * {
            width: 100%;
            height: 100%;
            min-height: 240px;
          }
        }

        /* ── Tiny Mobile ── */
        @media (max-width: 400px) {
          .ns-medium-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </>
  );
}

function BigCardContent({ bigCard, isMasterCard }) {
  return (
    <div className="ns-big-card-content">
      <div className="ns-big-card-image">
        <img
          src={isMasterCard ? bigCard.HM_Image : bigCard.image}
          alt={isMasterCard ? bigCard.HM_Title : bigCard.title}
        />
      </div>
      <div className="ns-big-card-body">
        <span
          style={{
            fontSize: "12px",
            fontWeight: 700,
            color: "#0066cc",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          {isMasterCard ? "BA News" : bigCard.source}
        </span>
        <div className="ns-big-card-title">
          {isMasterCard ? bigCard.HM_Title : bigCard.title}
        </div>
        <div className="ns-big-card-desc">
          {isMasterCard ? bigCard.HM_Description : bigCard.description}
        </div>
        <a
          href={
            isMasterCard
              ? `/homemaster/${bigCard.id}`
              : bigCard.NewsSource || bigCard.link
          }
          target={isMasterCard ? "_self" : "_blank"}
          rel={isMasterCard ? "" : "noopener noreferrer"}
          className="ns-big-card-readmore"
        >
          Read More →
        </a>
      </div>
    </div>
  );
}

export default NewsSection;
